import type { Camera2D } from '../hex/camera2d';
import { getTileAtWorldPoint, isPointInRect } from '../hex/hitTest';
import type { Point, Tile } from '../hex/types';
import type { MapModel } from '../map/mapModel';
import type { HexMapState } from '../render/renderer';
import {
    clampRectToViewport,
    getHeaderRect,
    getModalSize,
    isMobileViewport,
} from '../render/modalLayout';
import { applyActionBarAction, getActionBarActionAtPoint } from './actionController';
import { SettlementModal } from '../../modal/settlement/SettlementModal';
import { TileModal } from '../../modal/tile/TileModal';
import { ModalManager } from '../../modal/ModalManager';
import { isTutorialTileAllowed } from '../../tutorials/runtime';

function pointerPos(evt: PointerEvent): Point {
    return { x: evt.clientX, y: evt.clientY };
}

export function createPointerHandlers(
    canvas: HTMLCanvasElement,
    camera: Camera2D,
    model: MapModel,
    state: HexMapState,
    getViewport: () => { width: number; height: number },
    markDirty: () => void,
): {
    onPointerDown: (e: PointerEvent) => void;
    onPointerMove: (e: PointerEvent) => void;
    onPointerUp: (e: PointerEvent) => void;
} {
    const modalManager = new ModalManager(state);
    const pointers = new Map<number, Point>();
    let pinchActive = false;
    let pinchDistance = 0;
    let modalScrollPointer: number | null = null;
    let modalScrollY = 0;

    const createModalForTile = (tile: Tile, p: Point) => {
        const viewport = getViewport();
        const size = getModalSize(viewport);
        const mobile = isMobileViewport(viewport);
        const x = mobile
            ? 0
            : Math.min(viewport.width - size.w - 16, Math.max(16, p.x - size.w * 0.5));
        const y = mobile ? 0 : Math.min(viewport.height - size.h - 16, Math.max(16, p.y - 24));
        const rect = { x, y, w: size.w, h: size.h };
        const settlement = state.settlementByTileId[tile.id];
        if (settlement) return new SettlementModal(tile, rect, settlement.id);
        return new TileModal(tile, rect, null);
    };
    const onPointerDown = (e: PointerEvent) => {
        state.pointer = pointerPos(e);
        state.audio.manager.unlock();
        pointers.set(e.pointerId, state.pointer);

        const actionHit = getActionBarActionAtPoint(state, state.pointer);
        if (actionHit) {
            applyActionBarAction(state, actionHit.id);
            markDirty();
            return;
        }
        const modalHit = modalManager.getModalAtPoint(state.pointer);
        if (state.tutorial && !state.tutorial.completed && !modalHit) {
            const world = camera.screenToWorld(state.pointer);
            const tile = getTileAtWorldPoint(model, world.x, world.y);
            if (tile && !isTutorialTileAllowed(state, tile.id)) {
                markDirty();
                return;
            }
            if (!tile && state.tutorial.lockCamera) {
                markDirty();
                return;
            }
        }

        if (pointers.size === 2) {
            const points = Array.from(pointers.values());
            pinchDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
            pinchActive = true;
            state.dragging = false;
        }

        canvas.setPointerCapture(e.pointerId);
        state.dragging = true;
        state.dragStart = pointerPos(e);
        state.camStart = { x: camera.offsetX, y: camera.offsetY };

        if (modalHit) {
            const { modal, index } = modalHit;
            modalManager.bringToFront(index);
            state.activeModalId = modal.id;
            state.dragging = false;

            const headerRect = getHeaderRect(modal.rect);
            if (modalManager.handleCloseOrAction(state.pointer, modal)) {
                markDirty();
                return;
            }

            const mobile = isMobileViewport(getViewport());
            if (!mobile && isPointInRect(state.pointer, headerRect)) {
                state.modalDragId = modal.id;
                state.modalDragOffset = {
                    x: state.pointer.x - modal.rect.x,
                    y: state.pointer.y - modal.rect.y,
                };
            } else if (mobile) {
                modalScrollPointer = e.pointerId;
                modalScrollY = state.pointer.y;
            }

            state.dragging = false;
            markDirty();
            return;
        }

        markDirty();
    };

    const onPointerMove = (e: PointerEvent) => {
        state.pointer = pointerPos(e);
        pointers.set(e.pointerId, state.pointer);

        if (pinchActive && pointers.size >= 2) {
            const points = Array.from(pointers.values());
            const nextDistance = Math.hypot(points[0].x - points[1].x, points[0].y - points[1].y);
            if (pinchDistance > 0 && nextDistance > 0) {
                const center = {
                    x: (points[0].x + points[1].x) / 2,
                    y: (points[0].y + points[1].y) / 2,
                };
                camera.zoomAt(center, nextDistance / pinchDistance);
                pinchDistance = nextDistance;
                markDirty();
            }
            return;
        }

        if (modalScrollPointer === e.pointerId) {
            const deltaY = modalScrollY - state.pointer.y;
            if (Math.abs(deltaY) > 0.5 && modalManager.handleScroll(state.pointer, deltaY)) {
                modalScrollY = state.pointer.y;
                markDirty();
            }
            return;
        }

        if (state.modalDragId) {
            const modal = state.modals.find((m) => m.id === state.modalDragId);
            if (modal) {
                const viewport = getViewport();
                const size = getModalSize(viewport);
                modal.rect = clampRectToViewport(
                    {
                        x: state.pointer.x - state.modalDragOffset.x,
                        y: state.pointer.y - state.modalDragOffset.y,
                        w: size.w,
                        h: size.h,
                    },
                    viewport,
                );
            }
            markDirty();
        } else if (state.dragging) {
            if (state.tutorial && state.tutorial.lockCamera && !state.tutorial.completed) {
                markDirty();
                return;
            }
            const dx = (state.pointer.x - state.dragStart.x) / camera.zoom;
            const dy = (state.pointer.y - state.dragStart.y) / camera.zoom;
            camera.offsetX = state.camStart.x + dx;
            camera.offsetY = state.camStart.y + dy;
            markDirty();
        } else {
            const world = camera.screenToWorld(state.pointer);
            const nextHover = getTileAtWorldPoint(model, world.x, world.y);
            if (nextHover !== state.hover) {
                state.hover = nextHover;
                markDirty();
            }
        }

        state.isModalHover = !!modalManager.getModalAtPoint(state.pointer);
        if (!state.isModalHover) {
            state.isModalHover = !!getActionBarActionAtPoint(state, state.pointer);
        }
    };

    const onPointerUp = (e: PointerEvent) => {
        if (canvas.hasPointerCapture(e.pointerId)) {
            canvas.releasePointerCapture(e.pointerId);
        }
        pointers.delete(e.pointerId);
        if (pointers.size < 2) {
            pinchActive = false;
            pinchDistance = 0;
        }
        if (modalScrollPointer === e.pointerId) {
            modalScrollPointer = null;
        }
        state.pointer = pointerPos(e);
        if (state.modalDragId) {
            state.modalDragId = null;
            markDirty();
            return;
        }

        if (state.dragging) {
            state.dragging = false;

            const dist = Math.hypot(
                state.pointer.x - state.dragStart.x,
                state.pointer.y - state.dragStart.y,
            );
            const threshold = isMobileViewport(getViewport()) ? 10 : 5;
            if (dist < threshold) {
                const world = camera.screenToWorld(state.pointer);
                const tile = getTileAtWorldPoint(model, world.x, world.y);
                if (tile) {
                    if (
                        state.tutorial &&
                        !state.tutorial.completed &&
                        !isTutorialTileAllowed(state, tile.id)
                    ) {
                        markDirty();
                        return;
                    }
                    const existingIndex = state.modals.findIndex((m) => m.id === tile.id);
                    if (existingIndex >= 0) {
                        modalManager.bringToFront(existingIndex);
                        state.activeModalId = tile.id;
                    } else {
                        const modal = createModalForTile(tile, state.pointer);
                        state.modals.push(modal);
                        state.activeModalId = modal.id;
                    }
                }
                markDirty();
            }
        }
    };

    return { onPointerDown, onPointerMove, onPointerUp };
}

export function bindPointerController(
    canvas: HTMLCanvasElement,
    camera: Camera2D,
    model: MapModel,
    state: HexMapState,
    getViewport: () => { width: number; height: number },
    markDirty: () => void,
): () => void {
    const handlers = createPointerHandlers(canvas, camera, model, state, getViewport, markDirty);

    canvas.addEventListener('pointerdown', handlers.onPointerDown);
    canvas.addEventListener('pointermove', handlers.onPointerMove);
    canvas.addEventListener('pointerup', handlers.onPointerUp);

    return () => {
        canvas.removeEventListener('pointerdown', handlers.onPointerDown);
        canvas.removeEventListener('pointermove', handlers.onPointerMove);
        canvas.removeEventListener('pointerup', handlers.onPointerUp);
    };
}
